import { Eye, EyeOff, Lock, Mail, Volleyball as Ball } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button, Field, Input } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { postAuthPath } from '@/lib/pending-invite';
import { useAuth } from '@/store/auth';

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuth((state) => state.login);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await login(email.trim(), password);
      navigate(postAuthPath('/'), { replace: true });
    } catch (caught) {
      // A API responde igual para senha errada e conta inexistente; a tela
      // mantém a mesma discrição em vez de tentar adivinhar qual foi.
      setError(
        caught instanceof ApiError ? caught.message : 'Não foi possível entrar. Tente de novo.',
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell
      title="Entrar"
      subtitle="Times equilibrados, presença organizada e caixa em dia."
      footer={
        <>
          Ainda não tem conta?{' '}
          <Link to="/criar-conta" className="font-semibold text-brand hover:underline">
            Criar conta
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="E-mail" htmlFor="email">
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-ink-3" />
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="voce@email.com"
              className="pl-9"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </div>
        </Field>

        <Field label="Senha" htmlFor="password" error={error ?? undefined}>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-ink-3" />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Sua senha"
              className="pl-9 pr-10"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              invalid={Boolean(error)}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              className="absolute right-3 top-3.5 text-ink-3 hover:text-ink-2"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </Field>

        <div className="-mt-1 text-right">
          <Link
            to="/esqueci-a-senha"
            className="text-[13px] font-semibold text-brand hover:underline"
          >
            Esqueci minha senha
          </Link>
        </div>

        <Button type="submit" size="lg" full loading={loading} disabled={!email || !password}>
          Entrar
        </Button>
      </form>
    </AuthShell>
  );
}

/** Moldura das telas de autenticação: painel de marca à esquerda, form à direita. */
export function AuthShell({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <div className="flex min-h-screen">
      {/* Painel de marca — some no celular, onde a tela é do formulário. */}
      <div className="hidden w-1/2 flex-col justify-between bg-brand p-12 lg:flex">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-on-brand/15">
            <Ball className="h-6 w-6 text-on-brand" aria-hidden />
          </span>
          <span className="text-xl font-extrabold text-on-brand">NossoRachão</span>
        </div>

        <div className="max-w-md">
          <h2 className="text-4xl font-extrabold leading-tight text-on-brand">
            Sua pelada organizada de ponta a ponta.
          </h2>
          <p className="mt-4 text-lg text-on-brand/85">
            Confirmação de presença, times equilibrados por nível, mensalidades e caixa do grupo —
            num lugar só.
          </p>

          <ul className="mt-8 space-y-3 text-on-brand/90">
            {[
              'Times sorteados com goleiro em cada lado',
              'Avaliações anônimas que ninguém rastreia',
              'Caixa e mensalidades com Pix',
            ].map((item) => (
              <li key={item} className="flex items-center gap-2.5 text-[15px]">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-on-brand/20 text-xs">
                  ✓
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <p className="text-sm text-on-brand/70">Pelada de segunda desde sempre.</p>
      </div>

      {/* Formulário */}
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand">
              <Ball className="h-7 w-7 text-on-brand" aria-hidden />
            </span>
          </div>

          <h1 className="text-2xl font-extrabold tracking-tight">{title}</h1>
          <p className="mb-6 mt-1 text-sm text-ink-2">{subtitle}</p>

          {children}

          {footer ? <p className="mt-6 text-center text-sm text-ink-2">{footer}</p> : null}
        </div>
      </div>
    </div>
  );
}
